/**
 * @fileoverview TipTap Block Indent Extension
 *
 * Adds a numeric `indent` level to block nodes (paragraphs, headings and list
 * items) that renders as `style="margin-left: Npx"`, giving the toolbar's
 * Increase/Decrease Indent buttons a Word-style left indent on ANY line of
 * text — not just bullets.
 *
 * For list items this also covers the cases ProseMirror's structural
 * `sinkListItem` can't handle: the first (and only) item in a list has no
 * previous sibling to nest under, so a plain `sinkListItem` silently no-ops.
 * The toolbar tries `sinkListItem` first (proper nested markup) and falls back
 * to this attribute.
 *
 * Behaviour mirrors Word: every press adds another level of left margin, and
 * outdent strips one level until it hits zero (at which point the toolbar's
 * `liftListItem` fallback lifts a bullet out of its list entirely).
 *
 * Each command walks the whole selection and shifts every matching block by its
 * OWN level, so a drag-selection spanning blocks at different indents keeps
 * their relative offsets instead of being flattened to one level. Nested
 * matches are skipped — indenting a list item must not also indent the
 * paragraph inside it, which would double the margin.
 *
 * The inline `margin-left` also round-trips cleanly through the Word
 * `altChunk` exporter and the PDF print CSS, since an inline declaration
 * outranks the stylesheet's `margin` shorthand.
 */

import { Extension, type CommandProps } from "@tiptap/core";

/** Pixels added per indent level. Mirrors typical word-processor behaviour. */
export const BLOCK_INDENT_STEP_PX = 24;

/** Block types that can carry an indent level. */
export const BLOCK_INDENT_TYPES = ["listItem", "paragraph", "heading"] as const;

/** CSS length units we can convert to px, keyed to their px-per-unit value. */
const PX_PER_UNIT: Record<string, number> = {
  px: 1,
  pt: 96 / 72,
  pc: 16,
  in: 96,
  cm: 96 / 2.54,
  mm: 96 / 25.4,
  // Approximations against the browser default. Only used to recover an indent
  // level from imported markup, so being a step out is better than dropping it.
  em: 16,
  rem: 16,
};

/**
 * Read an indent level off a block's inline `style`.
 *
 * `PreserveStyles` strips `margin-left` from paragraphs and headings so this
 * extension owns the property outright (see MOR-382), which means anything we
 * fail to parse here is lost. Word and Google Docs export indents in `pt` or
 * `in` rather than `px`, so every absolute length unit is converted before the
 * level is derived. Values that aren't a clean multiple of the step round to
 * the nearest level.
 */
export function parseIndentLevel(styleAttr: string | null): number {
  const px = parseIndentPx(styleAttr);
  const level = Math.round(px / BLOCK_INDENT_STEP_PX);
  return Number.isFinite(level) && level > 0 ? level : 0;
}

/** The `margin-left` of a block's inline style in px (0 when absent). */
export function parseIndentPx(styleAttr: string | null): number {
  if (!styleAttr) return 0;
  const match = styleAttr.match(
    /(?:^|;)\s*margin-left\s*:\s*(-?\d*\.?\d+)(px|pt|pc|in|cm|mm|em|rem)?\s*(?=;|$)/i,
  );
  if (!match) return 0;
  const value = parseFloat(match[1]);
  const px = value * (PX_PER_UNIT[(match[2] || "px").toLowerCase()] ?? 1);
  return Number.isFinite(px) && px > 0 ? px : 0;
}

/**
 * Exact `margin-left` (as written, e.g. "42.55pt") when it is not a clean
 * multiple of the indent step — Word uploads carry arbitrary indents and
 * hanging-indent list paragraphs need `margin-left` to match their
 * `text-indent` exactly, so quantising to 24px steps would misalign the
 * clause text with its marker box. Returns null when the level round-trips.
 */
export function parseExactIndent(styleAttr: string | null): string | null {
  if (!styleAttr) return null;
  const match = styleAttr.match(
    /(?:^|;)\s*margin-left\s*:\s*(-?\d*\.?\d+)(px|pt|pc|in|cm|mm|em|rem)?\s*(?=;|$)/i,
  );
  if (!match) return null;
  const px = parseIndentPx(styleAttr);
  if (px <= 0) return null;
  const nearest = Math.round(px / BLOCK_INDENT_STEP_PX) * BLOCK_INDENT_STEP_PX;
  if (Math.abs(nearest - px) < 0.5) return null;
  return `${match[1]}${match[2] ?? "px"}`;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    blockIndent: {
      /** Bump every indentable block in the selection by +1 level. */
      increaseBlockIndent: () => ReturnType;
      /** Drop every indentable block in the selection by 1 level (clamped at 0). */
      decreaseBlockIndent: () => ReturnType;
    };
  }
}

interface BlockIndentOptions {
  /** Node type names the extension should apply to. */
  types: Array<string>;
  /** Maximum indent depth (prevents runaway margins). */
  maxLevel: number;
}

export const BlockIndent = Extension.create<BlockIndentOptions>({
  name: "blockIndent",

  addOptions() {
    return {
      types: [...BLOCK_INDENT_TYPES],
      maxLevel: 8,
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element: HTMLElement) =>
              parseIndentLevel(element.getAttribute("style")),
            renderHTML: (attributes: {
              indent?: number;
              indentExact?: string | null;
            }) => {
              if (attributes.indentExact) {
                return { style: `margin-left: ${attributes.indentExact}` };
              }
              const level = attributes.indent ?? 0;
              if (!level || level <= 0) return {};
              return {
                style: `margin-left: ${level * BLOCK_INDENT_STEP_PX}px`,
              };
            },
          },
          /**
           * Exact imported indent (see `parseExactIndent`). Rendered by the
           * `indent` attribute above; cleared by the indent commands, which
           * move to the nearest level from it.
           */
          indentExact: {
            default: null,
            parseHTML: (element: HTMLElement) =>
              parseExactIndent(element.getAttribute("style")),
            renderHTML: () => ({}),
          },
        },
      },
    ];
  },

  addCommands() {
    const indentable = new Set(this.options.types);
    const { maxLevel } = this.options;

    const shiftBy =
      (delta: number) =>
      () =>
      ({ tr, state, dispatch }: CommandProps) => {
        let changed = false;
        for (const range of state.selection.ranges) {
          state.doc.nodesBetween(
            range.$from.pos,
            range.$to.pos,
            (node, pos) => {
              if (!indentable.has(node.type.name)) return true;
              const current = (node.attrs.indent as number | undefined) ?? 0;
              // Growth stops at maxLevel, but an imported indent already past it
              // is left alone rather than yanked back on the first press.
              const next =
                delta > 0
                  ? Math.max(current, Math.min(maxLevel, current + delta))
                  : Math.max(0, current + delta);
              if (next !== current || node.attrs.indentExact) {
                changed = true;
                // `setNodeMarkup` keeps node sizes identical, so positions read
                // from `state.doc` stay valid for the rest of the walk.
                if (dispatch) {
                  tr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    indent: next,
                    indentExact: null,
                  });
                }
              }
              // Never descend into a block we just shifted — otherwise a list
              // item's inner paragraph would shift too and double the margin.
              return false;
            },
          );
        }
        return changed;
      };

    return {
      increaseBlockIndent: shiftBy(1),
      decreaseBlockIndent: shiftBy(-1),
    };
  },
});
